<template>
  <div class="task-emission" role="dialog" aria-modal="true" :aria-label="title">
    <section class="task-emission__card">
      <header class="d-flex justify-content-between align-items-center gap-3 border-bottom p-4">
        <div><h3 class="mb-1">{{ title }}</h3><strong>{{ task.patient.name }}</strong> · {{ task.title }}</div>
        <button type="button" class="btn btn-light" :disabled="sending" @click="$emit('close')">Voltar à tarefa</button>
      </header>
      <div class="task-emission__body p-4">
        <p v-if="loading" role="status">Carregando o último atendimento e os modelos...</p>
        <div v-else-if="error" class="alert alert-warning" role="alert">
          {{ error }} <button type="button" class="btn btn-sm btn-light" @click="load">Tentar novamente</button>
        </div>
        <template v-else-if="record">
          <div v-if="!quick" class="alert alert-primary">
            <strong>Emissão avulsa</strong> · Vinculada ao atendimento de {{ recordDate }}.
            <div>Crie um novo documento ou aproveite um anterior. Revise o conteúdo antes de assinar.</div>
          </div>
          <p v-if="notice" class="alert alert-warning" role="status">{{ notice }}</p>
          <div v-if="completed" class="alert alert-success" role="status">Documentos devolvidos para {{ task.author.name }}.</div>
          <label v-else class="d-flex align-items-center gap-2 mb-4">
            <input type="checkbox" v-model="autoReturn" :disabled="sending" />
            Após assinar, devolver automaticamente para {{ task.author.name }}
          </label>
          <PreviousDocumentPicker v-if="quick && !completed" :professional-id="professionalId" :patient-id="task.patient.id"
            :record-id="record.id_medrec" :task-context="context" :kind="kind"
            @created="onReissued" @empty="edit(emptyNotice)" @modify="edit()" />
          <div :class="{ 'task-emission__editor--hidden': quick && !completed }">
            <component :is="editor" ref="editor" :key="task.id + '-' + quick" :schedule="schedule" :medical-record="record" :task-context="context"
              :exam-protocols="examProtocols" :prescription-protocols="prescriptionProtocols" :document-templates="documentTemplates"
              :presc-types="prescTypes" :drug-admins="drugAdmins" :certifiers="certifiers" :readonly="completed"
              @signed="onSigned" @onModelSaved="loadModels" />
          </div>
          <p v-if="sendError" class="text-danger mt-3" role="alert">{{ sendError }}</p>
        </template>
      </div>
      <footer v-if="record && !completed && !(quick && autoReturn)" class="border-top p-4 d-flex justify-content-between align-items-center gap-3">
        <small>Os PDFs gerados acompanham a resposta. Arquivos sem assinatura digital serão identificados na devolução.</small>
        <button type="button" class="btn btn-primary" :disabled="sending" @click="complete">
          {{ sending ? 'Devolvendo...' : 'Devolver documentos gerados' }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import moment from 'moment';
import http from '@/helpers/http';
import { taskError } from './taskOptions';
import PreviousDocumentPicker from '@/views/attendance/shared/PreviousDocumentPicker.vue';

export default {
  components: {
    PreviousDocumentPicker,
    Prescription: defineAsyncComponent(() => import('@/views/attendance/tab/Prescription.vue')),
    ExamRequest: defineAsyncComponent(() => import('@/views/attendance/tab/ExamRequest.vue')),
    Document: defineAsyncComponent(() => import('@/views/attendance/tab/Document.vue')),
  },
  props: { task: { type: Object, required: true }, mode: { type: String, default: 'emit' } },
  emits: ['close', 'completed'],
  data() { return { quick: this.mode === 'reissue', notice: '', record: null, loading: false, error: '', sending: false, sendError: '', completed: false,
    autoReturn: true, examProtocols: [], prescriptionProtocols: [], documentTemplates: [], prescTypes: [], drugAdmins: [], certifiers: [] }; },
  computed: {
    placeId() { return this.$store.state.auth.place.id_place; },
    professionalId() { return this.$store.state.auth.user.id_prof; },
    endpoint() { return `/places/${this.placeId}/tasks/${this.task.id}/emission`; },
    editor() { return { prescription: 'Prescription', exam_request: 'ExamRequest', document: 'Document' }[this.task.type]; },
    kind() { return { prescription: 'prescriptions', exam_request: 'exam-requests', document: 'documents' }[this.task.type]; },
    title() {
      return (this.quick
        ? { prescription: 'Reemitir última prescrição', exam_request: 'Reemitir último pedido de exame', document: 'Reemitir último documento' }
        : { prescription: 'Emitir prescrição', exam_request: 'Fazer pedido de exame', document: 'Emitir documento' })[this.task.type];
    },
    emptyNotice() {
      return { prescription: 'Nenhuma prescrição anterior para reemitir. Crie uma nova abaixo.',
        exam_request: 'Nenhum pedido de exame anterior para reemitir. Crie um novo abaixo.',
        document: 'Nenhum documento anterior para reemitir. Crie um novo abaixo.' }[this.task.type];
    },
    context() { return { id: this.task.id, quick: this.quick, type: this.task.type, patientId: this.task.patient.id, patientName: this.task.patient.name, recordDate: this.recordDate, placeId: this.placeId, completed: this.completed }; },
    schedule() { return { id_prof: this.professionalId, id_patient: this.task.patient.id }; },
    recordDate() { return moment(this.record.medrec_date_time).format('DD/MM/YYYY [às] HH:mm'); },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const [{ data }] = await Promise.all([http.get(this.endpoint), this.loadModels()]);
        this.record = data;
      } catch (error) { this.error = taskError(error); }
      finally { this.loading = false; }
    },
    async loadModels() {
      const requests = [['certifiers', '/digital-certificates/certifiers']];
      if (this.task.type === 'prescription') requests.push(
        ['prescriptionProtocols', `/professionals/${this.professionalId}/prescription-models/actives`],
        ['prescTypes', '/prescription-types'], ['drugAdmins', '/drug-administrations']);
      if (this.task.type === 'exam_request') requests.push(['examProtocols', `/professionals/${this.professionalId}/exam-protocols`]);
      if (this.task.type === 'document') requests.push(['documentTemplates', `/places/${this.placeId}/professionals/${this.professionalId}/document-templates`]);
      await Promise.all(requests.map(async ([field, url]) => { this[field] = (await http.get(url)).data; }));
    },
    edit(notice = '') {
      this.notice = notice;
      this.quick = false;
    },
    onReissued(result) { return this.$refs.editor.onPreviousCopied(result); },
    onSigned() { if (this.autoReturn) this.complete(); },
    async complete() {
      if (this.sending || this.completed) return;
      this.sending = true;
      this.sendError = '';
      try {
        const { data } = await http.post(`${this.endpoint}/complete`);
        this.completed = true;
        this.$emit('completed', data);
      } catch (error) { this.sendError = taskError(error); }
      finally { this.sending = false; }
    },
  },
};
</script>

<style>
.task-emission { position: fixed; inset: 0; z-index: 1051; background: rgba(24,28,50,.4); display: flex; align-items: center; justify-content: center; padding: 1rem; }
.task-emission__card { width: min(1100px, 100%); max-height: calc(100vh - 2rem); background: white; border-radius: .85rem; display: flex; flex-direction: column; }
.task-emission .modal { background: rgba(24,28,50,.4); }
.task-emission__body { min-height: 0; overflow: auto; }
.task-emission__editor--hidden > :first-child { height: 0; margin: 0; padding: 0; border: 0; overflow: hidden; }
</style>
